<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'
import { useCurrencyStore } from '@/stores/currencyStore'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { useLegalLinks } from '@/router/legalLinks.js'

const legalPaths = useLegalLinks()

const props = defineProps({
  open:              { type: Boolean, default: false },
  total:             { type: Number,  default: 0 },     // in EvoGems (1 EvoGem = €1)
  payWithBalance:    { type: Boolean, default: false },  // true → charge internal balance, show EvoGems only
  needsDeliveryName: { type: Boolean, default: false },  // true while no EOS delivery target is linked
  submitting:        { type: Boolean, default: false },
  error:             { type: String,  default: '' },
})
const emit = defineEmits(['close', 'confirm'])

const currency = useCurrencyStore()
const symbol = computed(() => currency.symbol)
const rate   = computed(() => Number(currency.rate) || 1)

const checkout = ref({
  firstName: '', lastName: '', email: '',
  country: '', city: '', address: '', zipCode: '', phone: ''
})
const deliveryName = ref('')
const agreed = ref(false)
const deliveryAgreed = ref(false)

// Country dropdown — admin-managed list via public /api/countries.
const countries = ref([])
onMounted(async () => {
  try {
    const res = await api.get(EP.countries)
    if (Array.isArray(res.data)) countries.value = res.data
  } catch {}
})

function reset() {
  checkout.value = { firstName: '', lastName: '', email: '', country: '', city: '', address: '', zipCode: '', phone: '' }
  deliveryName.value = ''
  agreed.value = false
  deliveryAgreed.value = false
}
watch(() => props.open, (now) => { if (now) reset() })

// Charge shown in the selected currency + the EvoGem amount (e.g. €8.26 / 8.26 EvoGems).
const payDisplay  = computed(() => `${symbol.value}${(Number(props.total) * rate.value).toFixed(2)}`)
const gemsDisplay = computed(() => `${Number(props.total).toLocaleString('en-US', { maximumFractionDigits: 2 })} EvoGems`)

const canSubmit = computed(() => {
  const f = checkout.value
  if (!f.firstName.trim() || !f.lastName.trim() || !f.email.trim()) return false
  if (!f.country.trim() || !f.city.trim() || !f.address.trim()) return false
  if (!f.zipCode.trim() || !f.phone.trim()) return false
  if (props.needsDeliveryName && !deliveryName.value.trim()) return false
  if (!agreed.value || !deliveryAgreed.value) return false
  return true
})

function confirm() {
  if (!canSubmit.value || props.submitting) return
  emit('confirm', {
    billingInfo: { ...checkout.value },
    deliveryName: deliveryName.value.trim()
  })
}
function close() { if (!props.submitting) emit('close') }
</script>

<template>
  <Teleport to="body">
    <Transition name="co-fade">
      <div v-if="open" class="co-overlay" @click.self="close">
        <div class="co-modal" role="dialog" aria-modal="true" aria-labelledby="co-title">
          <button class="co-close" aria-label="Close" @click="close">×</button>

          <h2 id="co-title" class="co-title u-display u-allcaps">Billing details</h2>
          <p class="co-hint">
            <!-- Balance pays in internal currency → EvoGems is the headline, no fiat. -->
            <template v-if="payWithBalance">
              You pay: <strong class="u-gold">{{ gemsDisplay }}</strong>
            </template>
            <template v-else>
              You pay: <strong class="u-gold">{{ payDisplay }}</strong>
              <span class="co-gems">/ {{ gemsDisplay }}</span>
            </template>
          </p>

          <div class="co-form">
            <div class="co-row">
              <input v-model="checkout.firstName" class="co-input" placeholder="First name" />
              <input v-model="checkout.lastName"  class="co-input" placeholder="Last name" />
            </div>
            <input v-model="checkout.email"   class="co-input" type="email" placeholder="Email" />
            <select v-model="checkout.country" class="co-input co-select">
              <option value="" disabled>Country…</option>
              <option v-for="c in countries" :key="c.iso" :value="c.iso">{{ c.name }}</option>
            </select>
            <input v-model="checkout.city"    class="co-input" placeholder="City" />
            <input v-model="checkout.address" class="co-input" placeholder="Address" />
            <div class="co-row">
              <input v-model="checkout.zipCode" class="co-input" placeholder="ZIP code" />
              <input v-model="checkout.phone"   class="co-input" placeholder="Phone" />
            </div>

            <!-- Delivery target used until the account has a resolved EOS identity. -->
            <div v-if="needsDeliveryName" class="co-delivery">
              <label class="co-delivery-label u-display u-allcaps">In-game nickname or Steam ID</label>
              <input v-model="deliveryName" class="co-input" placeholder="Your exact in-game name" />
              <p class="co-delivery-note">
                Enter the exact name you use on this server. If you are offline,
                the order stays pending until you join with that name.
              </p>
            </div>

            <label class="co-agree">
              <input v-model="agreed" type="checkbox" />
              <span>
                I have read and agree to the
                <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" class="co-link" @click.stop>Terms of Use</a>
                and acknowledge the
                <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="co-link" @click.stop>Privacy Notice</a>.
                I understand that my personal data will be processed in accordance with the
                <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="co-link" @click.stop>Privacy Notice</a>.
              </span>
            </label>
            <label class="co-agree">
              <input v-model="deliveryAgreed" type="checkbox" />
              <span>I request immediate delivery and understand that I lose my 14-day right of withdrawal once delivery begins</span>
            </label>
          </div>

          <p v-if="error" class="co-error">{{ error }}</p>

          <ButtonSeed type="button" size="lg" class="co-submit" :disabled="submitting || !canSubmit" @click="confirm">
            {{ submitting ? 'PROCESSING…' : `PAY ${payWithBalance ? gemsDisplay : payDisplay}` }}
          </ButtonSeed>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.co-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
}
.co-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--bg-deep);
  border: 1px solid #2a2418;
  border-radius: var(--r-md, 8px);
  padding: var(--sp-8) var(--sp-6) var(--sp-6);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
}
.co-close {
  position: absolute;
  top: var(--sp-3);
  right: var(--sp-3);
  background: transparent;
  border: 0;
  color: var(--ink-soft);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
}
.co-close:hover { color: var(--gold); }
.co-title {
  font-size: clamp(var(--fs-18), 2vw, var(--fs-24));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0 0 var(--sp-2);
}
.co-hint {
  font-size: var(--fs-13);
  color: var(--ink-mute);
  margin: 0 0 var(--sp-5);
  line-height: 1.5;
}
.co-gems { margin-left: var(--sp-2); color: var(--ink-mute); }

.co-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.co-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
.co-input {
  width: 100%;
  background: var(--bg-deep);
  border: 1px solid var(--ash);
  border-radius: var(--r-xs, 4px);
  color: var(--ink);
  padding: 12px 14px;
  font-size: var(--fs-14);
  font-family: inherit;
  transition: border-color var(--t-fast) var(--ease-cinema);
}
.co-input:focus { outline: none; border-color: var(--gold); }
.co-select {
  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--gold) 50%),
                    linear-gradient(135deg, var(--gold) 50%, transparent 50%);
  background-position: calc(100% - 18px) center, calc(100% - 13px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 32px;
  cursor: pointer;
}
.co-select option { background: var(--bg-deep); color: var(--ink); }

.co-delivery {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--sp-4);
  border: 1px solid rgba(229, 199, 107, 0.28);
  border-radius: var(--r-xs);
  background: var(--gold-soft);
}
.co-delivery-label {
  font-size: var(--fs-11);
  color: var(--ink-mute);
  letter-spacing: 0.12em;
}
.co-delivery-note {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: 1.45;
}

.co-agree {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: 1.4;
  cursor: pointer;
  margin-top: var(--sp-1);
}
.co-agree input { margin-top: 3px; accent-color: var(--gold); }
.co-link {
  position: relative;
  z-index: 2;
  color: var(--gold);
  text-decoration: underline;
  text-underline-offset: 2px;
  pointer-events: auto;
}
.co-link:hover { color: var(--gold-deep, var(--gold)); }

.co-error {
  font-size: var(--fs-12);
  color: var(--ember);
  margin: var(--sp-2) 0 var(--sp-3);
  letter-spacing: 0.04em;
}
.co-submit { width: 100%; margin-top: var(--sp-3); }

@media (max-width: 500px) {
  .co-modal { padding: var(--sp-6) var(--sp-4) var(--sp-4); }
  .co-row   { grid-template-columns: 1fr; }
}

.co-fade-enter-active,
.co-fade-leave-active { transition: opacity 0.18s var(--ease-cinema); }
.co-fade-enter-from { opacity: 0; }
.co-fade-leave-to   { opacity: 0; }
</style>
